import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface KBMNewsItem {
  id: string;
  title: string;
  content: string;
  author: string;
  published_date: string;
  type: string;
  files?: { id: string; file_url: string; file_type: string }[];
}

export default function KBMNews() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [newsList, setNewsList] = useState<KBMNewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<KBMNewsItem | null>(null);
  const [viewingImage, setViewingImage] = useState<{ url: string; index: number; images: string[] } | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const { data, error } = await supabase
        .from('kbm_insights')
        .select('*')
        .eq('type', 'insight')
        .order('display_order', { ascending: false })
        .limit(6);

      if (error) throw error;

      const newsWithFiles = await Promise.all(
        (data || []).map(async (item) => {
          const { data: filesData } = await supabase
            .from('kbm_insights_files')
            .select('*')
            .eq('insight_id', item.id);
          return { ...item, files: filesData || [] };
        })
      );

      setNewsList(newsWithFiles);
    } catch (error) {
      console.error('Error fetching KBM news:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageFiles = (files?: { id: string; file_url: string; file_type: string }[]) => {
    return files?.filter(f => f.file_type === 'image') || [];
  };

  const openImageViewer = (url: string, images: string[], e: React.MouseEvent) => {
    e.stopPropagation();
    setViewingImage({ url, index: images.indexOf(url), images });
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (!viewingImage) return;
    const { index, images } = viewingImage;
    let newIndex = direction === 'prev' ? index - 1 : index + 1;
    if (newIndex < 0) newIndex = images.length - 1;
    if (newIndex >= images.length) newIndex = 0;
    setViewingImage({ url: images[newIndex], index: newIndex, images });
  };

  return (
    <>
      <section
        ref={sectionRef}
        id="kbm-news"
        className={`py-12 sm:py-16 md:py-24 bg-white transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Header */}
          <div className="mb-8 sm:mb-12">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#2F4F4F] rounded-lg sm:rounded-xl flex items-center justify-center">
                <i className="ri-megaphone-line text-lg sm:text-2xl text-white"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#2F4F4F]">
                킹덤브릿지 소식
              </h2>
            </div>
            <p className="text-xs sm:text-sm md:text-base text-gray-700 max-w-3xl leading-relaxed">
              킹덤브릿지선교회의 최신 소식과 활동을 전해드립니다.
            </p>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-8 sm:py-12">
              <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-gray-200 border-t-[#2F4F4F] rounded-full animate-spin"></div>
              <p className="text-gray-500 mt-3 sm:mt-4 text-sm sm:text-base">불러오는 중...</p>
            </div>
          ) : newsList.length === 0 ? (
            <div className={`bg-[#F5F5F0] rounded-xl sm:rounded-[20px] p-8 sm:p-12 md:p-16 text-center shadow-lg ${isVisible ? 'animate-kbm-fade-in' : 'opacity-0'}`}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-megaphone-line text-2xl sm:text-3xl md:text-4xl text-[#2F4F4F]"></i>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-[#2F4F4F] mb-3 sm:mb-4">
                소식 준비 중
              </h3>
              <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto leading-relaxed">
                킹덤브릿지선교회의 최신 소식이 곧 업로드됩니다.
              </p>

              <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6 sm:mt-10 max-w-lg mx-auto">
                <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-5">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2F4F4F]/10 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                    <i className="ri-calendar-event-line text-base sm:text-xl text-[#2F4F4F]"></i>
                  </div>
                  <h5 className="font-semibold text-gray-900 text-xs sm:text-sm mb-0.5 sm:mb-1">행사 소식</h5>
                  <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">선교 행사 안내</p>
                </div>
                <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-5">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2F4F4F]/10 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                    <i className="ri-team-line text-base sm:text-xl text-[#2F4F4F]"></i>
                  </div>
                  <h5 className="font-semibold text-gray-900 text-xs sm:text-sm mb-0.5 sm:mb-1">사역 소식</h5>
                  <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">사역 활동 보고</p>
                </div>
                <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-5">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2F4F4F]/10 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                    <i className="ri-heart-2-line text-base sm:text-xl text-[#2F4F4F]"></i>
                  </div>
                  <h5 className="font-semibold text-gray-900 text-xs sm:text-sm mb-0.5 sm:mb-1">감사 소식</h5>
                  <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">후원 감사 나눔</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {newsList.map((item, index) => {
                const imageFiles = getImageFiles(item.files);
                const imageUrls = imageFiles.map(f => f.file_url);

                return (
                  <div
                    key={item.id}
                    className={`bg-white rounded-xl sm:rounded-[20px] overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 cursor-pointer ${
                      isVisible ? 'animate-kbm-fade-in' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 100}ms` }}
                    onClick={() => setSelectedItem(item)}
                  >
                    {imageFiles.length > 0 && (
                      <div className="relative">
                        <div className="w-full h-40 sm:h-48 md:h-56 overflow-hidden">
                          <img
                            src={imageFiles[0].file_url}
                            alt={item.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        {imageFiles.length > 1 && (
                          <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-gray-600 flex items-center gap-1">
                            <i className="ri-image-line"></i>
                            {imageFiles.length}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="p-4 sm:p-5 md:p-6">
                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-[#2F4F4F] mb-1.5 sm:mb-2 line-clamp-2">
                        {item.title}
                      </h3>
                      {item.content && (
                        <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-3">
                          {item.content}
                        </p>
                      )}
                      <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-500">
                        <span>{item.author}</span>
                        <span>{new Date(item.published_date).toLocaleDateString('ko-KR')}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-4" onClick={() => setSelectedItem(null)}>
          <div className="bg-white rounded-xl sm:rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 pr-4 line-clamp-1">{selectedItem.title}</h3>
              <button onClick={() => setSelectedItem(null)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 cursor-pointer">
                <i className="ri-close-line text-xl sm:text-2xl text-gray-600"></i>
              </button>
            </div>
            <div className="p-4 sm:p-6">
              {(() => {
                const imageFiles = getImageFiles(selectedItem.files);
                const imageUrls = imageFiles.map(f => f.file_url);
                const pdfFiles = selectedItem.files?.filter(f => f.file_type === 'pdf') || [];

                return (
                  <>
                    {imageFiles.length > 0 && (
                      <div className="mb-6">
                        {imageFiles.length === 1 ? (
                          <img
                            src={imageFiles[0].file_url}
                            alt="첨부 이미지"
                            className="w-full max-h-[60vh] object-contain rounded-lg cursor-pointer hover:opacity-95 transition-opacity bg-gray-100"
                            onClick={(e) => openImageViewer(imageFiles[0].file_url, imageUrls, e)}
                          />
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {imageFiles.map((file, idx) => (
                              <img
                                key={file.id}
                                src={file.file_url}
                                alt={`첨부 이미지 ${idx + 1}`}
                                className="w-full h-48 sm:h-64 object-cover rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                                onClick={(e) => openImageViewer(file.file_url, imageUrls, e)}
                              />
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-2 text-center">이미지를 클릭하면 크게 볼 수 있습니다</p>
                      </div>
                    )}

                    <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 text-xs sm:text-sm text-gray-600">
                      <span className="flex items-center gap-1 sm:gap-2">
                        <i className="ri-user-line"></i>
                        {selectedItem.author}
                      </span>
                      <span className="flex items-center gap-1 sm:gap-2">
                        <i className="ri-calendar-line"></i>
                        {selectedItem.published_date}
                      </span>
                    </div>
                    <div className="prose max-w-none mb-4 sm:mb-6">
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedItem.content}</p>
                    </div>

                    {pdfFiles.length > 0 && (
                      <div className="border-t border-gray-200 pt-4 sm:pt-6">
                        <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">PDF 파일</h4>
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                          {pdfFiles.map((file) => (
                            <a
                              key={file.id}
                              href={file.file_url}
                              target="_blank"
                              rel="noopener noreferrer nofollow"
                              className="flex items-center justify-center h-24 sm:h-32 bg-red-50 rounded-lg border border-red-200 hover:bg-red-100 transition-colors cursor-pointer"
                            >
                              <i className="ri-file-pdf-line text-3xl sm:text-4xl text-red-600"></i>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer */}
      {viewingImage && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center"
          onClick={() => setViewingImage(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setViewingImage(null); }}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors z-10 cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-white"></i>
          </button>

          {viewingImage.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-3xl text-white"></i>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-3xl text-white"></i>
              </button>
            </>
          )}

          <img
            src={viewingImage.url}
            alt="이미지 보기"
            className="max-w-[95vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {viewingImage.images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 rounded-full text-white text-sm">
              {viewingImage.index + 1} / {viewingImage.images.length}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes kbm-fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-kbm-fade-in {
          animation: kbm-fade-in 0.6s ease-out forwards;
        }
      `}</style>
    </>
  );
}
